<template>
  <el-dialog
      :visible.sync="dialogVisibility"
      class="profile-modal"
      width="40%"
      top="10px">
    <div
        slot="title"
        style="text-align: center;">
      <div style="font-size: 1.2em; margin: 0px;">
        Create new Profile
      </div>
    </div>
    <el-form
        ref="form"
        class="new-device-profile"
        :model="newDevice"
        :rules="rules">
      <div v-if="errors">
        <el-alert
            v-for="(error, index) in errors"
            :key="index"
            style="margin-top: 3px"
            type="error">
          {{ error }}
        </el-alert>
      </div>
      <br>
      <el-form-item
          label="Product"
          prop="kind">
        <RefreshProducts style="float: right" />
        <el-select
            v-model="newDevice.kind"
            placeholder="Select one product"
            style="width: 100%;"
            clearable
            filterable
            @change="setProductOptions">
          <el-option
              v-for="product in devices"
              :key="product.id"
              :label="product.description"
              :value="product.kind" />
        </el-select>
      </el-form-item>
      <el-form-item
          :label="deviceIdLabel"
          :class="{'is-required': primaryIdentifier}"
          prop="device_id">
        <el-input v-model.trim="newDevice.device_id" />
      </el-form-item>
      <el-form-item
          label="Serial Number/SMSN"
          prop="serial_number">
        <el-input v-model.trim="newDevice.serial_number" />
      </el-form-item>
      <el-form-item
          v-if="!noProductColors"
          label="Color"
          prop="color">
        <el-select
            v-model="newDevice.color"
            placeholder="please select color"
            style="width: 100%;"
            :disabled="noProductColors">
          <el-option
              v-for="color in productColors"
              :key="color.abbr"
              :label="color.name"
              :value="color.name" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button
            type="primary"
            :disabled="working"
            @click="validateForm()">
          Create Profile
        </el-button>
        <el-button
            @click="closeForm()">
          Cancel
        </el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script>
import RefreshProducts from "@/components/Shared/Products/RefreshProducts";
import { createNamespacedHelpers as h } from "vuex";
import { EventBus } from "@/eventBus";
const { mapGetters: sharedGetters, mapActions: sharedActions } = h("shared");
import { keys, startCase } from "lodash";

let isHWId = (id) => /^[a-f0-9]{10}$/i.test(id);
let isMacId = (id) => /^[a-f0-9]{12}$/i.test(id);
let isDSN = (id) => /^[a-z0-9]{16}$/i.test(id);

export default {
  name: "ProfileCreator",
  components: {
    RefreshProducts,
  },
  props: {
    deviceId: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
  },
  data() {
    let colorValidator = (rule, value, callback) => {
      if (this.noProductColors) {
        return callback();
      } else {
        if (!value) return callback(new Error("Must select the color"));
        return callback();
      }
    };
    let deviceIdValidator = (rule, value, callback) => {
      if (this.useHWId) {
        if (isHWId(value)) return callback();
        else return callback(new Error("A valid Hardware Id is required"));
      }

      if (this.useMacId) {
        if (isMacId(value)) return callback();
        else return callback(new Error("A valid Mac Address is required"));
      }

      if (this.useDsnId) {
        if (isDSN(value)) return callback();
        else return callback(new Error("A valid DSN is required"));
      }

      if (!value || isHWId(value) || isMacId(value) || isDSN(value))
        return callback();
      else return callback(new Error("Invalid Mac Address/Hardware Id/DSN"));
    };

    return {
      primaryIdentifier: null,
      productColors: [],
      working: false,
      errors: [],
      rules: {
        kind: [
          { required: true, message: "Kind can't be blank", trigger: "blur" },
        ],
        device_id: [
          {
            validator: deviceIdValidator,
            required: true,
            message: "",
            trigger: "blur",
          },
        ],
        color: [
          {
            validator: colorValidator,
            trigger: "change",
          },
        ],
        serial_number: [
          {
            required: false,
            trigger: "blur",
          },
        ],
      },
      newDevice: {
        device_id: "",
        serial_number: "",
        kind: "",
        state: "",
        color: "",
        type: "device",
      },
    };
  },
  computed: {
    ...sharedGetters(["devices"]),
    deviceIdLabel() {
      let label = "Mac Address/Hardware ID/DSN";
      if (this.useHWId) label = "Hardware ID";
      if (this.useMacId) label = "Mac Address";
      if (this.useDsnId) label = "DSN";
      return label;
    },
    useHWId() {
      return this.primaryIdentifier === "use_hw_id";
    },
    useMacId() {
      return this.primaryIdentifier === "use_mac_id";
    },
    useDsnId() {
      return this.primaryIdentifier === "use_dsn_id";
    },
    dialogVisibility: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit("update:open", value);
      },
    },
    noProductColors() {
      return this.productColors.length === 0;
    },
  },
  created() {
    this.setNewDevice();
    this.fetchProducts();
  },
  methods: {
    ...sharedActions(["fetchProducts", "addProfile"]),
    setProductOptions(product_kind) {
      let product = this.devices.find((p) => p.kind === product_kind) || {};
      this.primaryIdentifier = product["primary_identifier"];
      if (product.options) {
        this.productColors = product.options.colors || [];
      } else {
        this.productColors = [];
        this.newDevice.color = null;
      }
    },
    closeForm() {
      this.$emit("update:open", false);
    },
    validateForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.create();
        } else {
          return false;
        }
      });
    },
    setNewDevice() {
      this.newDevice.device_id = this.deviceId;
    },
    create() {
      this.working = true;
      this.addProfile({ params: this.newDevice })
        .then((response) => {
          this.errors = [];
          EventBus.$emit("device-profile-only", response);
          this.closeForm();
        })
        .catch((error) => {
          this.errors = keys(error.data).map(
            (field) => `${startCase(field)} ${error.data[field].join(", ")}`
          );
        })
        .finally(() => {
          this.working = false;
        });
    },
  },
};
</script>
<style>
.refresh-products-positioning {
  position: absolute;
  margin-left: 14%;
  margin-top: 2%;
}
.profile-modal .el-dialog__body {
  padding: 10px 20px !important;
}
</style>
